import { inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { WhiteLabelAplicavelResponse } from 'app/shared/models/ei-models';
import { WhiteLabelService } from './white-label.service';

const CACHE_KEY = 'wl:current';
const CACHE_TTL_MS = 60 * 60 * 1000; // 1h

interface CachedWl {
    data: WhiteLabelAplicavelResponse;
    expiresAt: number;
}

@Injectable({ providedIn: 'root' })
export class WhiteLabelStateService {
    private wlService = inject(WhiteLabelService);

    /** Sinal global do WL atualmente aplicado (ou null se padrão Edduca). */
    readonly current = signal<WhiteLabelAplicavelResponse | null>(null);

    /** True quando há WL ativo aplicado — controla footer "Powered by" e logos dinâmicas. */
    readonly hasWhiteLabel = () => this.current() !== null;

    /**
     * Extrai o slug do hostname atual.
     * Exemplos:
     *   tokiomarine.edducaimob.com.br      → "tokiomarine"
     *   tokiomarine.hml.edducaimob.com.br  → "tokiomarine"
     *   edducaimob.com.br                  → null
     *   hml.edducaimob.com.br              → null
     *   tokiomarine.localhost              → "tokiomarine" (dev)
     *   localhost                          → null
     */
    extrairSlugDoHostname(hostname?: string): string | null {
        const host = (hostname ?? window.location.hostname).toLowerCase();

        // Hosts "padrão" Edduca (sem WL)
        const reservados = new Set([
            'edducaimob.com.br',
            'hml.edducaimob.com.br',
            'localhost',
            '127.0.0.1'
        ]);
        if (reservados.has(host)) return null;

        // Padrão prod: <slug>.edducaimob.com.br
        let m = host.match(/^([a-z0-9-]+)\.edducaimob\.com\.br$/);
        if (m && m[1] !== 'hml' && m[1] !== 'api' && m[1] !== 'www' && m[1] !== 'portal') return m[1];

        // Padrão hml: <slug>.hml.edducaimob.com.br
        m = host.match(/^([a-z0-9-]+)\.hml\.edducaimob\.com\.br$/);
        if (m) return m[1];

        // Padrão dev: <slug>.localhost
        m = host.match(/^([a-z0-9-]+)\.localhost$/);
        if (m) return m[1];

        return null;
    }

    /**
     * Pré-login: busca o WL pelo slug do subdomínio. Aplica cache local de 1h.
     */
    async carregarPorSlug(slug: string): Promise<void> {
        const cached = this.lerCache();
        if (cached && cached.data.slug === slug) {
            this.current.set(cached.data);
            return;
        }

        try {
            const wl = await firstValueFrom(this.wlService.obterPorSlug(slug));
            if (wl) {
                this.current.set(wl);
                this.salvarCache(wl);
            } else {
                this.current.set(null);
                this.limparCache();
            }
        } catch {
            // 404 ou erro → mantém branding Edduca padrão
            this.current.set(null);
            this.limparCache();
        }
    }

    /**
     * Pós-login: busca o WL aplicável seguindo a hierarquia (imobiliária Enterprise → cupom parceiro).
     */
    async carregarAplicavel(): Promise<void> {
        try {
            const wl = await firstValueFrom(this.wlService.obterAplicavel());
            if (wl) {
                this.current.set(wl);
                this.salvarCache(wl);
            } else {
                this.current.set(null);
                this.limparCache();
            }
        } catch {
            this.current.set(null);
            this.limparCache();
        }
    }

    /** Limpa o WL aplicado (ex: logout). */
    clear(): void {
        this.current.set(null);
        this.limparCache();
    }

    // ===== Cache =====

    private lerCache(): CachedWl | null {
        try {
            const raw = localStorage.getItem(CACHE_KEY);
            if (!raw) return null;
            const c = JSON.parse(raw) as CachedWl;
            if (c.expiresAt < Date.now()) {
                localStorage.removeItem(CACHE_KEY);
                return null;
            }
            return c;
        } catch {
            return null;
        }
    }

    private salvarCache(data: WhiteLabelAplicavelResponse): void {
        try {
            const c: CachedWl = { data, expiresAt: Date.now() + CACHE_TTL_MS };
            localStorage.setItem(CACHE_KEY, JSON.stringify(c));
        } catch { /* ignore quota errors */ }
    }

    private limparCache(): void {
        try { localStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
    }
}
